import React from 'react';
import { useForkRef, useUniqId } from '../../hooks';
import { SelectControl } from '../Select/components';
import { SelectPopup } from '../Select/components/SelectPopup/SelectPopup';
import { TreeList } from '../TreeList';
import { Flex } from '../layout';
import { useMobile } from '../mobile';
import { scrollToListItem, useList, useListState } from '../useList';
import { block } from '../utils/cn';
import { TreeSelectItem } from './TreeSelectItem';
import { useTreeSelectSelection, useValue } from './hooks/useTreeSelectSelection';
import './TreeSelect.css';
const b = block('tree-select');
const defaultItemRenderer = (renderState) => {
    return React.createElement(TreeSelectItem, Object.assign({}, renderState.props, renderState.renderContext));
};
export const TreeSelect = React.forwardRef(function TreeSelect({ id, qa, placement, slotBeforeListBody, slotAfterListBody, size, items, defaultOpen, width, containerRef: propsContainerRef, className, containerClassName, popupClassName, open: propsOpen, multiple, popupWidth, expandedById, disabledById, activeItemId, defaultValue, popupDisablePortal, groupsBehavior = 'expandable', value: propsValue, onClose, onUpdate, getId, onOpenChange, renderControl, renderItem = defaultItemRenderer, renderContainer, onItemClick, setActiveItemId: propsSetActiveItemId, mapItemDataToProps, }, ref) {
    const mobile = useMobile();
    const uniqId = useUniqId();
    const treeSelectId = id !== null && id !== void 0 ? id : uniqId;
    const controlWrapRef = React.useRef(null);
    const controlRef = React.useRef(null);
    const containerRefLocal = React.useRef(null);
    const containerRef = propsContainerRef !== null && propsContainerRef !== void 0 ? propsContainerRef : containerRefLocal;
    const handleControlRef = useForkRef(ref, controlRef);
    const { value, setInnerValue, selected } = useValue({
        value: propsValue,
        defaultValue,
    });
    const listState = useListState({
        expandedById,
        disabledById,
        activeItemId,
        selectedById: selected,
    });
    const setActiveItemId = propsSetActiveItemId !== null && propsSetActiveItemId !== void 0 ? propsSetActiveItemId : listState.setActiveItemId;
    const listParsedState = useList(Object.assign({ items,
        getId }, listState));
    const wrappedOnUpdate = React.useCallback((ids) => onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate(ids, ids.map((id) => listParsedState.itemsById[id])), [listParsedState.itemsById, onUpdate]);
    const { open, toggleOpen, handleClearValue, handleMultipleSelection, handleSingleSelection } = useTreeSelectSelection({
        setInnerValue,
        value,
        onUpdate: wrappedOnUpdate,
        defaultOpen,
        open: propsOpen,
        onClose,
        onOpenChange,
    });
    const handleItemClick = React.useCallback(({ id: listItemId, data, groupState, isLastItem, itemState }) => {
        const defaultHandleClick = () => {
            if (listState.disabledById[listItemId])
                return;
            // always activate selected item
            setActiveItemId(listItemId);
            if (groupState && groupsBehavior === 'expandable') {
                listState.setExpanded((state) => (Object.assign(Object.assign({}, state), { 
                    // toggle expanded state by id, by default all groups expanded
                    [listItemId]: typeof state[listItemId] === 'boolean' ? !state[listItemId] : false })));
            }
            else if (multiple) {
                handleMultipleSelection(listItemId);
            }
            else {
                handleSingleSelection(listItemId);
                toggleOpen(false);
            }
        };
        if (onItemClick) {
            return onItemClick({
                id: listItemId,
                data,
                groupState,
                itemState,
                isLastItem,
                disabled: listState.disabledById[listItemId],
                defaultClickCallback: defaultHandleClick,
            });
        }
        return defaultHandleClick();
    }, [
        onItemClick,
        listState,
        setActiveItemId,
        groupsBehavior,
        multiple,
        handleMultipleSelection,
        handleSingleSelection,
        toggleOpen,
    ]);
    // restoring focus when popup opens
    React.useLayoutEffect(() => {
        var _a;
        if (open) {
            const lastSelectedItemId = value[value.length - 1];
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setActiveItemId(lastSelectedItemId);
            if (lastSelectedItemId) {
                scrollToListItem(lastSelectedItemId, containerRef.current);
            }
        }
        // subscribe only in open event
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const handleClose = React.useCallback(() => toggleOpen(false), [toggleOpen]);
    const controlProps = {
        open,
        toggleOpen,
        clearValue: handleClearValue,
        ref: handleControlRef,
        size,
        value,
        id: treeSelectId,
        activeItemId: listState.activeItemId,
    };
    const togglerNode = renderControl ? (renderControl(controlProps)) : (React.createElement(SelectControl, Object.assign({}, controlProps, { selectedOptionsContent: React.Children.toArray(value.map((itemId) => mapItemDataToProps(listParsedState.itemsById[itemId]).title)).join(', '), view: "normal", pin: "round-round", popupId: `tree-select-popup-${treeSelectId}`, selectId: `tree-select-${treeSelectId}` })));
    const mods = Object.assign({}, (width === 'max' && { width }));
    const inlineStyles = {};
    if (typeof width === 'number') {
        inlineStyles.width = width;
    }
    return (React.createElement(Flex, { direction: "column", gap: "5", ref: controlWrapRef, className: b(mods, className), style: inlineStyles },
        togglerNode,
        React.createElement(SelectPopup, { ref: controlWrapRef, className: b('popup', popupClassName), controlRef: controlRef, width: popupWidth, placement: placement, open: open, handleClose: handleClose, disablePortal: popupDisablePortal, mobile: mobile, id: `tree-select-popup-${treeSelectId}` },
            slotBeforeListBody,
            React.createElement(TreeList, { size: size, className: containerClassName, qa: qa, multiple: multiple, id: `list-${treeSelectId}`, containerRef: containerRef, getId: getId, disabledById: listState.disabledById, selectedById: listState.selectedById, expandedById: listState.expandedById, activeItemId: listState.activeItemId, setActiveItemId: setActiveItemId, onItemClick: handleItemClick, items: items, renderContainer: renderContainer, mapItemDataToProps: mapItemDataToProps, renderItem: renderItem !== null && renderItem !== void 0 ? renderItem : defaultItemRenderer }),
            slotAfterListBody)));
});
